/**
 * #2088, what the Analyst panel remembers between visits, per user.
 *
 * Kept React-free and side-effect-free at the top so the parse/clamp rules are
 * unit-testable without a DOM. Storage is per user id: two people on one browser
 * (a shared plant terminal) must not inherit each other's panel width, and a
 * signed-out read must not leak the last user's state.
 *
 * #2307: conversationId rides in the same per-user slot as open/width, so a
 * hard reload can rehydrate the conversation the panel was showing rather
 * than greeting the user with "New conversation" where their thread was.
 */

export const MIN_PANEL_WIDTH = 320;
export const MAX_PANEL_WIDTH = 640;
export const DEFAULT_PANEL_WIDTH = 384;

export interface AnalystPanelPrefs {
  open: boolean;
  width: number;
  conversationId: string | null;
}

export const DEFAULT_PANEL_PREFS: AnalystPanelPrefs = {
  open: false,
  width: DEFAULT_PANEL_WIDTH,
  conversationId: null,
};

/** Storage key for a user. Anonymous (no session yet) gets its own throwaway slot. */
export function panelPrefsKey(userId: string | null | undefined): string {
  return `crosbe-analyst-panel:${userId || "anon"}`;
}

/** Any input to a width inside the resize bounds. Junk falls back to the default. */
export function clampPanelWidth(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_PANEL_WIDTH;
  return Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, Math.round(n)));
}

/** Never throws: a corrupt blob reads as the defaults rather than breaking the shell. */
export function parsePanelPrefs(raw: string | null | undefined): AnalystPanelPrefs {
  if (!raw) return { ...DEFAULT_PANEL_PREFS };
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_PANEL_PREFS };
  }
  if (obj === null || typeof obj !== "object" || Array.isArray(obj)) return { ...DEFAULT_PANEL_PREFS };
  const { open, width, conversationId } = obj as { open?: unknown; width?: unknown; conversationId?: unknown };
  return {
    open: open === true,
    width: clampPanelWidth(width),
    conversationId: typeof conversationId === "string" && conversationId.length > 0 ? conversationId : null,
  };
}

export function readPanelPrefs(userId: string | null | undefined): AnalystPanelPrefs {
  try {
    return parsePanelPrefs(localStorage.getItem(panelPrefsKey(userId)));
  } catch {
    return { ...DEFAULT_PANEL_PREFS };
  }
}

export function writePanelPrefs(userId: string | null | undefined, prefs: AnalystPanelPrefs): void {
  try {
    localStorage.setItem(
      panelPrefsKey(userId),
      JSON.stringify({
        open: prefs.open,
        width: clampPanelWidth(prefs.width),
        conversationId: prefs.conversationId ?? null,
      }),
    );
  } catch {
    // A private window with storage blocked keeps the panel working for the session.
  }
}
